<script lang="ts">
    import { GeoJSONSource, GeoJSONSourceSpecification, Map } from "maplibre-gl";
    import { _, locale  } from "svelte-i18n";
    import * as turf from "@turf/turf";
    import { afterUpdate } from "svelte";
    import { navigate } from "svelte-navigator";
    import type { SnapshotCommitData } from "../api/data/SnapshotCommitData";
    import { appManager } from "../AppManager";
    import { get } from "svelte/store";
    import SearchOnMap from "../components/map/search/SearchOnMap.svelte";
    import { NewProjectModalManager } from "./NewProjectModalManager";
    import type { Polygon } from "geojson";

    export let open = false;
    const modalClose = () => {
        open = false;
    };

    const manager: NewProjectModalManager = new NewProjectModalManager(locale);
    manager.getMapViewStore().subscribe(view => {
        if (typeof view === "undefined") return;

        map.flyTo(view);
    });

    let mapContainer: HTMLElement;
    let map: Map;

    let commits: SnapshotCommitData[] = [];
    let commit: {  id: string };
    let zoomed: boolean = false;
    let canCreate: boolean = false;

    let ownerOptions: {
        id: string;
        type: "user" | "organization";
        name: string;
    }[] = [];
    let ownerId: string;
    let showMapHelp: boolean = true;

    let name: string;
    let nameManual: boolean = false;

    let planet: {  id: string };

    $: if (typeof name === "undefined" || name.length == 0) {
        nameManual = false;
    }

    $: canCreate =
        typeof commit !== "undefined" &&
        zoomed &&
        typeof name !== "undefined" &&
        name.length > 0 &&
        typeof ownerId !== "undefined" &&
        ownerId.length > 0;

    $: showMapHelp = !zoomed;

    $: if (!open) {
        map = undefined;
    }

    const onNameKeyDown = async () => {
        if (typeof name !== "undefined" && name.length > 0) {
            nameManual = true;
        }
    };

    afterUpdate(() => {
        if (
            typeof map === "undefined" &&
            typeof mapContainer !== "undefined" &&
            open
        ) {
            map = new Map({
                container: mapContainer,
                hash: false,
                style: "https://api.maptiler.com/maps/0d19e9c6-3baf-467f-abde-db0e28870f27/style.json?key=OZUCIh4RNx38vXF8gF4H",
                center: [0, 0],
                zoom: 0.2,
            });

            zoomed = false;
            showMapHelp = true;
            canCreate = false;

            map.resize();

            map.on("dragend", async () => await zoomEnd());
            map.on("zoomend", async () => await zoomEnd());

            map.on("load", async () => {
                commits =
                    await appManager.api.edit.getLatestSnapshotCommits({
                        accessToken:
                            await appManager.api.getAccessToken(),
                    });

                const planetCommit = commits.find((x) => x.snapshot.name === "planet");
                if (typeof planetCommit !== "undefined") {
                    planet = await getLatestCachedCommit(planetCommit);
                }
                if (typeof planet === "undefined") {
                    commits = commits.filter(
                        (x) =>
                            typeof x.snapshot.extentPolygon !== "undefined" &&
                            x.snapshot.extentPolygon !== null
                    );

                    // build negative polygon.
                    const coordinates: number[][][] = [];
                    coordinates.push([
                        [-180, -85],
                        [180, -85],
                        [180, 85],
                        [-180, 85],
                        [-180, -85],
                    ]);
                    commits.forEach((sc) => {
                        coordinates.push(
                            (sc.snapshot.extentPolygon.geometry as Polygon).coordinates[0]
                        );
                    });
                    const feature = turf.polygon(coordinates);
                    const featureCollection = turf.featureCollection([feature]);

                    map.addSource("snapshot-extents", {
                        type: "geojson",
                        data: featureCollection,
                    } as unknown as GeoJSONSourceSpecification);

                    map.addLayer({
                        id: "snapshot-extents",
                        type: "fill",
                        source: "snapshot-extents",
                        layout: {},
                        paint: {
                            "fill-color": "#0080ff",
                            "fill-opacity": 0.7,
                        },
                    });
                }
            });
        }
    });

    async function getLatestCachedCommit(commitData: SnapshotCommitData): Promise<{ id: string } | undefined> {
        const commitIdReady = await appManager.api.publish.getSnapshotStatus({
            snapshotId: commitData.snapshot.id,
            accessToken:
                await appManager.api.getAccessToken()
        });
        if (typeof commitIdReady === "undefined") return undefined;

        return {
            id: commitIdReady.snapshotCommitId
        }
    }

    async function zoomEnd() {
        const bounds = map.getBounds();
        const polygon = turf.polygon([
            [
                [bounds.getWest(), bounds.getNorth()],
                [bounds.getEast(), bounds.getNorth()],
                [bounds.getEast(), bounds.getSouth()],
                [bounds.getWest(), bounds.getSouth()],
                [bounds.getWest(), bounds.getNorth()],
            ],
        ]);
        const area = turf.area(polygon);
        if (typeof planet !== "undefined") {
            commit = planet;
        } else {
            commit = commits.find((c) => {
                return turf.booleanContains(c.snapshot.extentPolygon, polygon);
            });
        }

        zoomed = map.getZoom() >= 12;

        if (!nameManual) {
            const zoom = map.getZoom();

            if (zoomed) {
                const point = map.project(map.getCenter());
                const width = 80;
                const height = 80;
                const features = map.queryRenderedFeatures(
                    [
                        [point.x - width / 2, point.y - height / 2],
                        [point.x + width / 2, point.y + height / 2],
                    ],
                    {
                        //layers: [ "place"]
                        filter: ["has", "name"],
                    }
                );

                name = "";
                if (features.length > 0) {
                    for (let i = 0; i < features.length; i++) {
                        let feature = features[i];
                        if (feature.sourceLayer !== "place") continue;

                        name = feature.properties.name;
                        break;
                    }
                }
            } else {
                name = "";
            }
        }
    }

    let creating: boolean = false;

    async function onCreate() {
        creating = true;

        const bounds = map.getBounds();
        const polygon = turf.polygon([
            [
                [bounds.getWest(), bounds.getNorth()],
                [bounds.getEast(), bounds.getNorth()],
                [bounds.getEast(), bounds.getSouth()],
                [bounds.getWest(), bounds.getSouth()],
                [bounds.getWest(), bounds.getNorth()],
            ],
        ]);
        const owner = ownerOptions.find((o) => o.id == ownerId);
        const organizationId =
            owner.type === "organization" ? owner.id : undefined;
        const accessToken = await appManager.api.getAccessToken()

        const scenario0Branch = await appManager.api.edit.createBranch({
            accessToken,
            extentPolygon: polygon,
            snapshotCommitId: commit.id,
        });

        const scenario1Branch = await appManager.api.edit.copyBranch({
            accessToken: await appManager.api.getAccessToken(),
            branchId: scenario0Branch.id,
        });

        const newProject = await appManager.api.projects.createProject({
            accessToken,
            name,
            organizationId,
            area: polygon.geometry,
            scenarios: [
                {
                    branchId: scenario0Branch.id,
                    name:  $_("impact.project.newModal.scenario0.name"),     
                    description:  $_("impact.project.newModal.scenario0.description"),          
                    functionalName: "0",
                },
                {
                    branchId: scenario1Branch.id,
                    name:  $_("impact.project.newModal.scenario1.name"),     
                    description:  $_("impact.project.newModal.scenario1.description"),         
                    functionalName: "1",
                }
            ],
        });

        // Create a default route planner
        await appManager.api.projects.createReroute({
            accessToken,
            data: {
                projectId: newProject.id,
                name: $_("impact.project.routeplanner.namenew"),
                scenarios: [
                    newProject.scenarios[0].id,
                    newProject.scenarios[1].id
                ]
            }
        });

        const navigateTarget = `${appManager.PublicUrl}/scenario/${newProject.scenarios[1].id}`;
        navigate(navigateTarget);
    }

    $: if (open) {
        fetchData().then();
    }

    async function fetchData() {
        const newOwnerOptions: {
            id: string;
            type: "user" | "organization";
            name: string;
        }[] = [];

        let isAdmin = false;
        const userDetails = get(await appManager.getCurrentUserStore());
        if (typeof userDetails !== "undefined") {
            isAdmin = userDetails.isAdmin;
        }

        let organizations = await appManager.api.projects.getUserOrganizations({
            accessToken:
                await appManager.api.getAccessToken(),
        });

        organizations.sort((x1, x2) => {
            return (x1.userIsMember ? 0 : 1) - (x2.userIsMember ? 0 : 1);
        });

        organizations.forEach((o) => {
            newOwnerOptions.push({
                id: o.id,
                name: o.name,
                type: "organization",
            });
        });

        ownerOptions = newOwnerOptions;
        if (newOwnerOptions.length > 0) ownerId = newOwnerOptions[0].id;
    }
</script>

{#if open}
    <div
        class="modal"
        id="newproject"
        tabindex="-1"
        role="dialog"
        aria-labelledby="newprojectLabel"
        aria-hidden="false">
        <div class="modal-dialog modal-xl" role="document">
            <div class="modal-content">
                <div class="modal-body p-6">
                    <button
                        type="button"
                        class="btn-close"
                        aria-label="Close"
                        on:click={modalClose} />

                    <h2 class="font-weight-bold text-center mb-1">
                        <!-- {$_("impact.project.newModal.title")} -->
                    </h2>

                    <div class="map">
                        <div class="w-100 h-100" bind:this={mapContainer}>
                            <slot />
                        </div>
                        {#if showMapHelp}
                        <div
                            class="instructions-overlay">
                            <!-- {$_(
                                "impact.project.newModal.zoomInMore"
                            )} -->
                        </div>
                        {/if}
                        <div
                            class="bounds-overlay {zoomed
                                ? ''
                                : 'd-none'}" />
                        <SearchOnMap {manager} i18nPrefix={"impact.project.newModal."}/>
                    </div>

                    <div>
                        <div class="row">
                            <div class="col-12 col-md-12">
                                <div class="form-floating">
                                    <select
                                        bind:value={ownerId}
                                        class="form-control form-control-flush"
                                        id="organization">
                                        {#each ownerOptions as o}
                                            <option value={o.id}
                                                >{@html o.name}</option>
                                        {/each}
                                    </select>
                                    <label for="organization"
                                        >{$_(
                                            "impact.project.newModal.owner"
                                        )}</label>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-12 col-md-12">
                                <div class="form-floating">
                                    <input
                                        type="text"
                                        class="form-control form-control-flush"
                                        name="name"
                                        bind:value={name}
                                        on:keypress={async () =>
                                            await onNameKeyDown()}
                                        placeholder="Name" />
                                    <label for="name"
                                        >{$_(
                                            "impact.project.newModal.name"
                                        )}</label>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-12">
                                <!-- Submit -->
                                <div class="d-grid gap-2">
                                    <button
                                        class="btn btn-block btn-primary mt-3 lift {!canCreate || creating
                                            ? 'disabled'
                                            : ''}"
                                        on:click={async () => await onCreate()}>
                                        {$_("impact.project.newModal.confirm")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div class="modal-backdrop show" on:click={modalClose} />
{/if}

<style>
    .map {
        height: calc(100vh - 400px);
        width: 100%;
        border-radius: 0.5rem;
        position: relative;
    }

    .instructions-overlay {
        left: 0%;
        right: 0%;
        top: 0%;
        bottom: 0%;
        position: absolute;
        pointer-events: none;
        padding: 50px 0;
        background-color: #0003;
        text-align: center;
        color: white;
        font-size: x-large;
    }

    .bounds-overlay {
        left: 50px;
        right: 50px;
        top: 150px;
        bottom: 50px;
        position: absolute;
        border: dotted;
        border-width: 1px;
        border-radius: .375rem;
        pointer-events: none;
        border-color: grey;
    }
    .modal {
        display: block;
    }
</style>
